import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, addDays } from 'date-fns';
import { serialize } from 'object-to-formdata';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ImageAvatarImage } from '~/assets/images';
import { Input, InputMask, InputDate } from '~/components';
import { useAuth } from '~/hooks/auth';
import { User } from '~/models';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';
import handleChangeFile from '~/utils/handleFile';
import { regex_remove_all_non_digits } from '~/utils/regex';

import {
  Header,
  HeaderLabel,
  SaveButton,
  SettingContainer,
  FieldSet,
  ImageLabel,
  Image,
  ImageInput,
  InputsContainer,
  Label,
} from './styles';

type FileForm = {
  attachment: File;
};

type FormProps = {
  name: string;
  email: string;
  cellphone: string;
  address_country: string;
  address_state: string;
  address_city: string;
  address_neighborhood: string;
  address_zip_code: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  image_url: string;
  cpf: string;
  birth_date: string;
  genre: string;
  public_name: string;
  professional_record: string;
};

export const MyData = () => {
  const formRef = useRef<FormHandles>(null);
  const { user, updateUser } = useAuth();

  const [image, setImage] = useState<FileForm | null>(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [preview, setPreview] = useState<any>('');
  const [defaultData, setDefaultData] = useState({} as FormProps);

  // Setta dados iniciais do formulário
  useEffect(() => {
    const initialData = {
      ...user,
      birth_date: format(
        addDays(new Date(user.professional.birth_date), 1),
        'yyyy-MM-dd',
      ),
      genre: user.professional.genre,
      public_name: user.professional.public_name,
      cref: user.professional.cref,
    };

    formRef.current?.setFieldValue('cpf', user.professional.cpf ?? '');
    formRef.current?.setFieldValue('cellphone', user.cellphone ?? '');
    formRef.current?.setFieldValue(
      'address_zip_code',
      user.address_zip_code ?? '',
    );

    console.log(`initialData`, initialData);

    setDefaultData(initialData as unknown as FormProps);

    setPreview(user.image_url);
  }, [user]);

  const handleChangeImage = useCallback(
    async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const newFile = handleChangeFile(e);
      if (newFile) {
        setImage({
          attachment: newFile.file,
        });

        const toBase64 = () =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(newFile.file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
          });

        setPreview(await toBase64());
      }
    },
    [],
  );

  const getAddressByZipCode = useCallback(async (zipCode: string) => {
    try {
      const data = await fetch(`https://viacep.com.br/ws/${zipCode}/json`).then(
        (response) => response.json(),
      );

      if (data.cep) {
        formRef.current?.setFieldValue('address_country', 'Brasil');
        formRef.current?.setFieldValue('address_state', data.uf);
        formRef.current?.setFieldValue('address_city', data.localidade);
        formRef.current?.setFieldValue('address_street', data.logradouro);
        formRef.current?.setFieldValue('address_neighborhood', data.bairro);
      } else {
        toast.error('CEP não localizado, insira o endereço manualmente!');

        formRef.current?.setFieldValue('address_country', '');
        formRef.current?.setFieldValue('address_state', '');
        formRef.current?.setFieldValue('address_city', '');
        formRef.current?.setFieldValue('address_street', '');
        formRef.current?.setFieldValue('address_neighborhood', '');
      }
    } catch (error) {
      console.log(error.message);
    }
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      setLoading(true);

      let imageUrl;

      if (image?.attachment)
        try {
          const imageFormData = serialize<FileForm>(image);

          const { data: imageData } = await api.post(
            `/attachment/profile-image`,
            imageFormData,
            {
              headers: {
                'Context-Type': 'multipart/form-data',
              },
            },
          );

          imageUrl = imageData.image_url;
        } catch (err) {
          console.log(err);

          toast.error('Ocorreu um erro inesperado!');

          return;
        }

      const schema = Yup.object().shape({
        public_name: Yup.string().required('O campo é obrigatório'),
        name: Yup.string().required('O campo é obrigatório'),
        cpf: Yup.string().required('O campo é obrigatório'),
        birth_date: Yup.string().required('O campo é obrigatório '),
        email: Yup.string().required('O campo é obrigatório'),
        cellphone: Yup.string().required('O campo é obrigatório'),
      });

      const formData = {
        ...data,
        cpf: data.cpf.replace(regex_remove_all_non_digits, ''),
        cellphone: data.cellphone.replace(regex_remove_all_non_digits, ''),
        address_zip_code: data.address_zip_code.replace(
          regex_remove_all_non_digits,
          '',
        ),
        image_url: imageUrl ?? preview,
      };

      try {
        await schema.validate(formData, { abortEarly: false });

        const { data: apiData } = await api.put<User>(
          `users/${user.id}`,
          formData,
        );

        updateUser(apiData);

        setLoading(false);

        toast.success('Perfil atualizado com sucesso!');
      } catch (err) {
        console.log(err);

        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          toast.error('Preencha os campos corretamente!');
        } else {
          toast.error('Ocorreu um erro inesperado!');
        }
      }
    },
    [updateUser, image, preview, user],
  );

  const inputStyle = {
    padding: '0 1.6rem',
    height: '3.8rem',
  };

  return (
    <>
      <Header>
        <HeaderLabel>Meus dados</HeaderLabel>
        <SaveButton
          disabled={loading}
          onClick={() => formRef.current?.submitForm()}
        >
          Salvar
        </SaveButton>
      </Header>

      <SettingContainer>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={defaultData}>
          <FieldSet>
            <ImageLabel htmlFor="logo">
              <p>Foto:</p>
              {image ?? preview ? (
                <Image src={preview} alt="Imagem do usuário" />
              ) : (
                <Image src={ImageAvatarImage} alt="Imagem do usuário" />
              )}
            </ImageLabel>

            <ImageInput alt="student" id="logo" onChange={handleChangeImage} />
          </FieldSet>

          <InputsContainer>
            <FieldSet>
              <Label>Nome público: </Label>
              <Input
                containerStyle={inputStyle}
                name="public_name"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Nome: </Label>
              <Input
                containerStyle={inputStyle}
                name="name"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>CPF: </Label>
              <InputMask
                containerStyle={inputStyle}
                name="cpf"
                mask="999.999.999-99"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Registro Profissional: </Label>
              <Input
                containerStyle={inputStyle}
                name="cref"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Data de nascimento: </Label>
              <InputDate
                containerStyle={inputStyle}
                name="birth_date"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>E-mail: </Label>
              <Input
                containerStyle={inputStyle}
                name="email"
                type="email"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Celular: </Label>
              <InputMask
                containerStyle={inputStyle}
                name="cellphone"
                mask="(99) 99999-9999"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>CEP: </Label>
              <InputMask
                containerStyle={inputStyle}
                name="address_zip_code"
                mask="99999-999"
                onChange={(e) => {
                  e.target.value.split('_').join('').length === 9
                    ? getAddressByZipCode(e.target.value)
                    : false;
                }}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Endereço: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_street"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Cidade: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_city"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Bairro: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_neighborhood"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Estado: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_state"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>País: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_country"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Número: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_number"
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <Label>Complemento: </Label>
              <Input
                containerStyle={inputStyle}
                name="address_complement"
                fontSize="1.5rem"
              />
            </FieldSet>
          </InputsContainer>
        </Form>
      </SettingContainer>
    </>
  );
};
